/**
 * Gli eventi sportivi per tipologia.
 *
 * `events(kind)` del prototipo v3 filtrava i programmi della guida per tipologia
 * scartando quelli finiti, e ordinava **prima i live, poi per orario**. Quella è la
 * regola, e vale identica. Cambia da dove arrivano i dati — adesso da
 * `GET /api/v1/app/events/:kind` — e cambia chi decide «live»: il prototipo leggeva un
 * campo `p.live` precalcolato, qui non c'è e non può esserci (05-CONTRATTO-API §6.1).
 *
 * Gli otto campi che il player riceve, e nient'altro: `id`, `title`, `start`, `end`,
 * `competition`, `home`, `away`, `channels[]`. Legare un'app installata su dodicimila
 * televisori alla forma interna di uno scraper vorrebbe dire non poter più cambiare né
 * l'una né l'altra (05-CONTRATTO-API §6.2).
 */

import { eventLiveness, type EventTimes, type Liveness, type LivenessSource } from './liveness.js';

export interface SportEvent extends EventTimes {
  readonly id: string;
  readonly title: string;
  readonly competition: string;
  readonly home: string | null;
  readonly away: string | null;
  /**
   * Cosa riprodurre. Un nome di emittente non è un riferimento: al player serve il
   * canale, non l'etichetta.
   */
  readonly channelIds: ReadonlyArray<string>;
}

export interface SportEventView {
  readonly event: SportEvent;
  readonly state: Liveness;
  /** Se il verdetto viene dalla guida EPG o dalla fine stimata dallo scraper. */
  readonly source: LivenessSource;
}

/**
 * Su quale canale **questa linea** può guardare l'evento, o `null` se su nessuno.
 *
 * ## Il contratto lo prometteva e nessuno lo manteneva
 *
 * `app:events:<tipologia>` è **una chiave sola per tutta l'installazione**: i provider raccolgono una
 * volta e tutte le linee leggono lo stesso elenco. Filtrarla per linea vorrebbe dire 717 chiavi al
 * posto di due, e infatti il contratto dice l'opposto — «il player intersecca questi identificativi
 * con il proprio elenco canali, che ha già, filtrato per i suoi pacchetti, e offre quello che
 * possiede» (`AppEventChannel`, lato backend).
 *
 * **Il player non intersecava niente.** Prendeva `channelIds[0]`, cioè il primo che il server aveva
 * messo in fila, e lo apriva. Su una linea che quel canale non lo vende il risultato è il peggiore
 * possibile: la card si disegna, il distintivo dice «in diretta», si preme OK e non parte niente.
 * Nessun errore, perché nessuno ha sbagliato — semplicemente quel canale non è suo.
 *
 * ## Quale, quando sono più d'uno
 *
 * Succede per davvero, e in due modi: lo stesso evento va in onda su due emittenti (Sky e DAZN), e
 * la stessa emittente può essere mappata su due canali del catalogo — l'alta definizione e la
 * normale. Prendere il primo è una scelta fatta dall'ordine di una query.
 *
 * Qui si sceglie il **numero di canale più basso fra quelli che la linea possiede**, ed è l'unica
 * preferenza dichiarata che esista in questo sistema: il numero lo decide l'operatore, ed è l'ordine
 * in cui ha deciso di presentare il proprio listino. A parità di numero vince l'ordine dell'evento,
 * che è quello della guida.
 *
 * **Non si indovina la qualità dal nome.** Cercare «HD» nei titoli sceglierebbe sempre qualcosa, e un
 * canale sbagliato non si vede che è sbagliato: si apre, e chi guarda pensa che sia quello giusto.
 */
export function playableChannelOf<T extends { readonly id: string; readonly number: number }>(
  event: SportEvent,
  owned: ReadonlyArray<T>,
): T | null {
  if (event.channelIds.length === 0) return null;

  const byId = new Map(owned.map((channel) => [channel.id, channel]));
  let best: T | null = null;
  for (const id of event.channelIds) {
    const channel = byId.get(id);
    if (channel === undefined) continue;
    if (best === null || channel.number < best.number) best = channel;
  }
  return best;
}

/**
 * Gli eventi non ancora finiti, i live per primi e poi per orario d'inizio.
 * Prototipo v3: `sort((a, b) => (b.live - a.live) || (a.s - b.s))`.
 */
export function upcomingEvents(
  events: ReadonlyArray<SportEvent>,
  serverNowMs: number,
): ReadonlyArray<SportEventView> {
  return events
    .map((event) => {
      const verdict = eventLiveness(event, serverNowMs);
      return { event, state: verdict.state, source: verdict.source };
    })
    .filter((view) => view.state !== 'past')
    .sort((a, b) => {
      const liveFirst = Number(b.state === 'live') - Number(a.state === 'live');
      if (liveFirst !== 0) return liveFirst;
      return a.event.startMs - b.event.startMs;
    });
}

export function liveCount(views: ReadonlyArray<SportEventView>): number {
  return views.filter((view) => view.state === 'live').length;
}

/**
 * Il primo che comincia, fra quelli non ancora cominciati.
 *
 * Serve allo stato vuoto che dice quando finirà di esserlo: «Nessuna partita in diretta
 * in questo momento · prossima alle 21:30». **Uno stato vuoto che dice quando finirà di
 * esserlo non è uno stato vuoto** (03-SCHERMATE-TV §6).
 */
export function nextToStart(views: ReadonlyArray<SportEventView>): SportEventView | null {
  const future = views.filter((view) => view.state === 'future');
  if (future.length === 0) return null;
  return future.reduce((earliest, view) =>
    view.event.startMs < earliest.event.startMs ? view : earliest,
  );
}
